import React, { Component } from 'react';
import { inject, observer } from "mobx-react";
import MoneyInput from './money-input';

import {cachedFetch} from '../utils/cached-api-wrapper';
import DistanceInput from './distance-input';
import { evaluateExpression, shouldEvaluate } from '../utils/expression-evaluator/index';
// XPEN-3467: this suggestion list is hand-rolled rather than a library dropdown,
// but it had the same constraint — `w-100` pinned it to the field's width.
import { dropdownWidthStyle } from '../utils/dropdown-width';

function shallowEqualParams(a, b) {
    if (a === b) return true;
    if (a == null || b == null) return a == b;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
        if (a[k] !== b[k]) return false;
    }
    return true;
}

export default inject("data")(observer(class FormTextfield extends Component {
    
    constructor(props) {
        super(props);

        this.state = {
            selected: false,
            startSaving: false,
            value: this._capValue(this.props.value === null ? "" : this.props.value),
            invalidValue: false,
            wasModified: false,
            searchPropositions: null,
            typedValue: "",
            showSearchResults: true, // Controls visibility of search results
        }

        if(props.passClearRecommendationFunc)
            props.passClearRecommendationFunc(() => this.setState({searchPropositions: null}));

        this.cachedSearchResults = {};
        this.searchTimeoutId = null;
        this.searchResultsRef = React.createRef();
        this.editableRef = React.createRef();

    }

    formatMoney(number) {
        let result = this.props.data.t.read("n_a");

        if (number != null && number !== "") {
            if (isNaN(number) || this.state.invalidValue) {
                result = number;
            }
            else {
                result = "$ " + parseFloat(number).toFixed(2);
            }
        }

        return result;
    }

    formatData(data) {
        if (data === null || data === undefined || data.length === 0) {
            if (this.props.hideNA === null || !this.props.hideNA) {
                data = this.props.data.t.read("n_a");
            }
        }

        return data;
    }

    formatDate(data) {
        if (data == null || data.length === 0) {
            if (this.props.hideNA == null || !this.props.hideNA) {
                data = this.props.data.t.read("n_a");
            }
        }

        return data;
    }

    shouldUseRawDateValue() {
        return this.props.format === "date" && (this.state.selected || this.props.data?.isMobile);
    }

    normalizeDateValue(value) {
        if (value == null || value === "") return "";
        const str = String(value);
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
        if (str.includes("T")) return str.split("T")[0];
        if (str.includes(" ")) return str.split(" ")[0];
        const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
            const mm = match[1].padStart(2, "0");
            const dd = match[2].padStart(2, "0");
            const yyyy = match[3];
            return `${yyyy}-${mm}-${dd}`;
        }
        return str;
    }

    getValue() {
        let val = this.state.value;

        if (!this.state.selected) {
            if (this.props.format === "data") {
                val = this.formatData(val);
            }
            else if (this.props.format === "money") {
                val = this.formatMoney(val);
            }
            else if (this.props.format === "date") {
                if (!this.shouldUseRawDateValue()) {
                    val = this.formatDate(val);
                }
            }
        }

        if(val === undefined)
            return "";

        return val;
    }

    close() {
        this.setState({
            value: this._capValue(this.props.value == null ? "" : this.props.value),
            selected: false,
            wasModified: false
        })
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.state.startSaving && !this.props.locked && prevProps.locked) {
            this.setState({
                startSaving: false,
                wasModified: false,
            })
        }
        else if (prevProps.value !== this.props.value) {
            const nextValue = this._capValue(this.props.value == null ? "" : this.props.value);
            this.setState({
                value: nextValue
            }, () => {
                if (this.editableRef.current && this.props.format === "textarea-div") {
                    this.editableRef.current.innerText = nextValue;
                }
            });
        }

        if (prevState.selected === true && !this.state.selected) {
            if (!this.state.invalidValue && this.state.wasModified && ((this.state.value + "" !== (this.props.value == null ? "" : this.props.value)) || this.state.startSaving) && !this.props.locked) {
                this.save()
            }
        }

        if (!shallowEqualParams(prevProps.searchParams, this.props.searchParams)) {
            this.cachedSearchResults = {};
            this.setState({ searchPropositions: null, showSearchResults: false });
        }
    }

    valueChanged(value) {
        this.setState({
            value: value,
            wasModified: true,
        }, () => {
            if (this.props.format === "money") {
                if (this.state.value !== "" && isNaN(this.state.value)) {
                    if (!this.state.invalidValue) {
                        this.setState({
                            invalidValue: true,
                        })
                    }
                }
                else if (this.state.invalidValue) {
                    this.setState({
                        invalidValue: false,
                    })
                }
            }
        })

    }

    save() {
        if (!this.state.invalidValue) {
            this.setState({
                startSaving: this.props.locked !== null //keep it false if no locked props was given, otherwise make it true 
            }, () => {
                    if (this.props.validateChange) {
                        this.props.validateChange(this.state.value)
                    }
            })
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.searchTimeoutId != null) {
            clearTimeout(this.searchTimeoutId);
            this.searchTimeoutId = null;
        }

        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (this.searchResultsRef.current && !this.searchResultsRef.current.contains(event.target)) {
            this.setState({ showSearchResults: false });
        }
    };


    componentDidMount() {
        this._isMounted = true;
        document.addEventListener('mousedown', this.handleClickOutside);
        if (this.editableRef.current) {
            this.editableRef.current.innerText = this.getValue() || "";
        }
    }

    keyTyped(val) {
        const originalVal = val;
        val = val.trim().replace(/\//g, '');
        
        if (this.props.searchURL != null) {

            if (val.length > 0) {
                if (this.props.splitValues === true) {
                    if (val.includes(',')) {
                        val = val.split(',').pop().trim();
                    }
                }
                
                if (this.cachedSearchResults[val] == null || !this.cachedSearchResults[val].length) {
                    cachedFetch(this.props.searchParams ? this.props.searchURL : `${this.props.searchURL}${encodeURIComponent(val)}`, "GET", this.props.searchParams ? {...this.props.searchParams, text: val} : {},
                        response => {
                            const raw = response.data;
                            const list = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
                            this.cachedSearchResults[val] = list;
                            // Avoid setState on unmounted component when search response arrives after close/navigation
                            if (!this._isMounted) return;
                            this.setState({
                                typedValue: originalVal,
                                searchPropositions: list
                            });
                        });
                }
                else {
                    this.setState({
                        typedValue: originalVal,
                        searchPropositions: this.cachedSearchResults[val]
                    });
                }
            }
            else {
                this.setState({
                    typedValue: originalVal,
                    searchPropositions: null
                })
            }
        }
    }

    selectSearchProposition(e, proposition) {
        let val = this.state.value?.trim() || '';

        if (this.props.splitValues && val.includes(',')) {
            const values = val.split(',').map(v => v.trim());
            values[values.length - 1] = proposition.name;
            val = values.join(', ');
        } else {
            val = proposition.name;
        }

        this.setState({
            searchPropositions: null,
            value: val,
            selected: false,
            startSaving: true
        }, () => {
            const { searchCallback } = this.props;
            if (searchCallback) searchCallback(proposition);
            else this.save();
        });
    }


    addSearchProposition() {
        this.cachedSearchResults = {};
        this.props.searchCallback(null, this.state.typedValue);
    }

    reset(){

    }

    evaluateMathOperations(rawValue) {
        if (!this.props.mathOperations) return rawValue;

        const expression = rawValue == null ? "" : String(rawValue);
        if (!shouldEvaluate(expression)) return rawValue;

        const result = evaluateExpression(expression);
        if (result !== null && !isNaN(result) && isFinite(result)) {
            return String(result);
        }

        return rawValue;
    }

    // A textarea-div with a maxLength must never DISPLAY more than the limit, not
    // only block new typing — so a pre-existing over-limit value (e.g. an
    // engine-extracted description) shows exactly what will be exported, with no
    // surprise truncation later. Other fields / no maxLength are untouched.
    _capValue(value) {
        const max = this.props.maxLength;
        if (this.props.format === "textarea-div" && typeof max === "number"
            && typeof value === "string" && value.length > max) {
            return value.slice(0, max);
        }
        return value;
    }

    handleTextAreaDivInput = (e) => {
        let text = e.currentTarget.innerText;
        // Hard character cap for the contenteditable div (native maxLength does
        // not apply): trim typed/pasted overflow and keep the caret at the end.
        // Skip mid-IME-composition so a CJK/accent session isn't corrupted; the
        // composed text is trimmed on the following (non-composition) input.
        const max = this.props.maxLength;
        if (typeof max === "number" && text.length > max && !e.nativeEvent?.isComposing) {
            text = text.slice(0, max);
            e.currentTarget.innerText = text;
            const selection = window.getSelection();
            if (selection) {
                const range = document.createRange();
                range.selectNodeContents(e.currentTarget);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
        this.valueChanged(text); // your existing method
    };

    handleBlur = (value) => {
        const evaluatedValue = this.evaluateMathOperations(value);
        if (evaluatedValue !== value) {
            this.valueChanged(evaluatedValue);
        }

        this.setState({selected: false});
    };

    render() {
        const isDateInput = this.shouldUseRawDateValue();
        let inputType = isDateInput ? "date" : this.props.format !== 'date' ? this.props.format : "text";
        if (this.props.mathOperations && inputType === "number") {
            inputType = "text";
        }
        const rawValue = this.getValue();
        const inputValue = isDateInput ? this.normalizeDateValue(rawValue) : (rawValue ? rawValue.toString() : "");
        const ariaLabel = this.props["aria-label"] || this.props.ariaLabel;

        return <div className="form-field d-flex">
            {
                this.props.format === "textarea" ?
                    <textarea disabled={this.props.locked} onFocus={() => this.setState({selected: true})}
                              onChange={e => this.valueChanged(e.target.value)}
                              onBlur={e => this.handleBlur(e.target.value)}
                              className={`form-control form-control-sm ${this.props.classes} ${this.props.highlight ? "border-highlight": ""} ${this.state.selected ? "field-selected" : "field-unselected"} ${this.state.invalidValue ? "text-danger" : ""}`}
                              style={{maxWidth: "100%"}} value={this.getValue()}
                              placeholder={this.props.placeholder}
                              aria-label={ariaLabel}
                              required={this.props.required}/>
                    :
                    this.props.format === "textarea-div" ?
                        <div
                            ref={this.editableRef}
                            contentEditable={!this.props.locked}
                            onInput={this.handleTextAreaDivInput}
                            onFocus={() => this.setState({selected: true})}
                            onBlur={(e) => {
                                const text = e.currentTarget.innerText;
                                const evaluatedValue = this.evaluateMathOperations(text);
                                if (evaluatedValue !== text) {
                                    e.currentTarget.innerText = evaluatedValue;
                                    this.valueChanged(evaluatedValue);
                                }

                                this.setState({selected: false});
                            }}
                            suppressContentEditableWarning
                            className={`textarea-div pt-2 pb-2 form-control form-control-sm ${this.props.classes} ${this.props.highlight ? "border-highlight" : ""} ${this.state.selected ? "field-selected" : "field-unselected"}${this.state.invalidValue ? "text-danger" : ""}`}
                            style={{maxWidth: "100%"}}
                            role="textbox"
                            aria-label={ariaLabel}
                            aria-required={this.props.required}
                            data-required={this.props.required}
                        />
                        :
                    this.props.format === "money" ?
                    <MoneyInput isReadOnly={this.props.locked} className={`form-control ${this.props.classes || ''}`} value={(this.state.value !== "" && this.state.value != null && parseFloat(this.state.value).toFixed(2)) || null}
                     onChange={e => this.props.validateChange && this.props.validateChange(e)} locale={`${this.props.data.t.lang}-${this.props.data.t.lang.toUpperCase()}`} 
                     placeholder={this.props.placeholder || this.props.data.t.read('n_a')}
                     required={this.props.required}
                      />
                    :
                    this.props.format === "distance" ?
                    <DistanceInput value={(this.state.value && parseFloat(this.state.value).toFixed(2)) || null}
                     onChange={e => this.props.validateChange && this.props.validateChange(e)} locale={`${this.props.data.t.lang}-${this.props.data.t.lang.toUpperCase()}`} 
                     placeholder={this.props.placeholder || this.props.data.t.read('n_a')}
                     required={this.props.required}
                      />
                    :
                    <input
                        onKeyUp={e => this.keyTyped(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                e.target.blur();
                            }
                        }}
                        type={inputType}
                        max={this.props.format === "date" ? "9999-12-31" : null}
                        maxLength={this.props.maxLength}
                        disabled={this.props.locked}
                        onBlur={e => this.handleBlur(e.target.value)}
                        onFocus={e => {
                            this.setState({selected: true, showSearchResults: true});
                            if (this.props.searchURL && this.props.searchOnFocus && e.target.value) {
                                this.keyTyped(e.target.value);
                            }
                        }}
                        onChange={e => this.valueChanged(e.target.value)}
                        className={`form-control form-control-sm ${this.props.classes} ${this.props.highlight ? "border-highlight": ""} ${this.state.selected ? "field-selected" : "field-unselected"} ${this.state.invalidValue ? "text-danger" : ""}`}
                        style={{maxWidth: "100%", fontSize:this.props.fontSize}}
                        value={inputValue}
                        placeholder={this.props.placeholder}
                        aria-label={ariaLabel}
                        required={this.props.required}
                    />
            }
            {
                (this.state.showSearchResults && this.state.searchPropositions != null &&
                    (this.state.searchPropositions.length !== 0 || this.props.hideNoResults == null || !this.props.hideNoResults)) ?
                    <div className="search-results" style={dropdownWidthStyle('100%')} ref={this.searchResultsRef}>
                        {
                            (this.state.searchPropositions && Array.isArray(this.state.searchPropositions) && this.state.searchPropositions.length > 0) ?
                                <React.Fragment>
                                    {
                                        this.state.searchPropositions.map(proposition => {
                                            return <div className='mb-2 pointer' key={proposition._id}
                                                        onClick={e => this.selectSearchProposition(e, proposition)}>
                                                <span>{proposition.name}</span>
                                                {proposition.address && this.props.searchURL.includes("suppliers/search") && 
                                                    <p className="font-italic font-xxsmall"> {proposition.address} </p>}
                                                {proposition.description &&
                                                    <span className="font-italic font-xxsmall"> {proposition.description} </span>}
                                            </div>
                                        })
                                    }
                                    <div className='font-italic semi-faded mt-1'>
                                        {
                                            this.props.hideAddOperation == null || !this.props.hideAddOperation ?
                                                <div>
                                                    {this.props.data.t.read("if_result_not_present")}
                                                    <span className='text-primary' role="button" onClick={() => this.addSearchProposition()}> {this.props.data.t.read("add_it")}</span>
                                                </div>
                                                :
                                                this.props.noAddOperationHint ?
                                                    <div>{this.props.noAddOperationHint}</div>
                                                    :
                                                    null
                                        }
                                    </div>
                                </React.Fragment>
                                :
                                <div className='font-italic semi-faded mt-1'>
                                    <span>
                                        {this.props.data.t.read("no_results_found")}
                                        {
                                            this.props.hideAddOperation == null || !this.props.hideAddOperation ?
                                                <span className='text-primary' role="button" onClick={() => this.addSearchProposition()}> {this.props.data.t.read("add_it")}</span>
                                                :
                                                null
                                        }
                                    </span>
                                    {
                                        // XPEN-3267: when the caller suppressed "add it", it can
                                        // explain why here instead of leaving a dead end.
                                        (this.props.hideAddOperation && this.props.noAddOperationHint) ?
                                            <div className='mt-1'>{this.props.noAddOperationHint}</div>
                                            :
                                            null
                                    }
                                </div>
                        }
                    </div>
                    :
                    null
            }
        </div>
    }
}));
